import { type FC, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { getMarketsAnalyticsEvents, useAdditionalNav } from '@mwl/core-lib';
import { Button } from '@/components/Button/Button';
import { useMediaWidthDown } from '@/hooks';
import { MarketsFilterAdditional } from '../MarketsFilterAdditional/MarketsFilterAdditional';
import type { MarketsFilterProps } from './MarketsFilter.types';
import styles from './MarketsFilter.module.scss';
const MarketsFilter: FC<MarketsFilterProps> = ({
  filters,
  counts,
  totalCount,
  activeFilter,
  onFilterClick
}) => {
  const {
    t
  } = useTranslation('sport');
  const rootRef = useRef<HTMLDivElement>(null);
  const shadowRef = useRef<HTMLDivElement>(null);
  const isScreenXs = useMediaWidthDown('screen850');
  const sortedFilters = useMemo(() => {
    if (!filters) {
      return [];
    }
    return [...filters].sort((a, b) => a.weight - b.weight);
  }, [filters]);
  const {
    visibleLinks,
    otherLinks
  } = useAdditionalNav({
    links: sortedFilters,
    containerRef: rootRef,
    shadowRef,
    numberRemovedLinks: 1
  });
  const {
    select,
    unselect
  } = getMarketsAnalyticsEvents(totalCount, counts[(activeFilter as number)]);
  const renderFilters = useMemo(() => {
    if (isScreenXs) {
      return filters;
    }
    return visibleLinks;
  }, [isScreenXs, visibleLinks, filters]);
  useEffect(() => {
    if (activeFilter && counts[activeFilter] === 0) {
      onFilterClick(null);
    }
  }, [activeFilter, counts, onFilterClick]);
  if (!filters || !totalCount) {
    return null;
  }
  return <div className={styles.root} ref={rootRef} data-sentry-component="MarketsFilter" data-sentry-source-file="MarketsFilter.tsx">
      <div className={styles.shadowLinks} ref={shadowRef}>
        <Button as="button" size="sm" fontSize="sm" variant="text" analytics={select} className={styles.item} data-sentry-element="Button" data-sentry-source-file="MarketsFilter.tsx">
          {t('line.filter.all', 'All')}
          <span className={styles.count}>{totalCount}</span>
        </Button>
        {filters?.map(filter => {
        if (!counts[filter.id]) {
          return null;
        }
        return <Button as="button" size="sm" fontSize="sm" key={filter.id} variant="text" className={styles.item}>
              {filter.title}
              <span className={styles.count}>{counts[filter.id]}</span>
            </Button>;
      })}
      </div>
      <Button as="button" size="sm" fontSize="sm" variant="text" analytics={select} className={cn(styles.item, {
      [styles.active]: !activeFilter
    })} onClick={() => onFilterClick(null)} data-sentry-element="Button" data-sentry-source-file="MarketsFilter.tsx">
        {t('line.filter.all', 'All')}
        <span className={styles.count}>{totalCount}</span>
      </Button>
      {renderFilters?.map(filter => {
      if (!counts[filter.id]) {
        return null;
      }
      return <Button as="button" size="sm" fontSize="sm" key={filter.id} variant="text" analytics={activeFilter === filter.id ? unselect : select} className={cn(styles.item, {
        [styles.active]: activeFilter === filter.id
      })} onClick={() => onFilterClick(filter.id)}>
            {filter.title}
            <span className={styles.count}>{counts[filter.id]}</span>
          </Button>;
    })}
      {!isScreenXs && <MarketsFilterAdditional filters={otherLinks.filter(link => counts[link.id])} activeFilter={activeFilter} onFilterClick={onFilterClick} counts={counts} totalCount={totalCount} />}
    </div>;
};
export { MarketsFilter };